import Icon from '@components/common/assets';
import { useInterview } from '@components/pass/interview/start/context/interviewContext';
import Colors from '@domain/constant/colors';
import { CompanyQuestionVO } from '@domain/vo/pass/interview/question/CompanyQuestionVO';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { useCompanyList } from '@repository/interviewPractices/pooledQuestions/usePooledQuestions';
import useCheckAuthority from '@utils/hooks/useCheckAuthority';
import classnames from 'classnames/bind';
import React, { useEffect, useState } from 'react';
import style from './companyList.module.scss';

const MockData = [
  {
    sn: 0,
    name: '국민은행',
    openYn: true,
  },
  {
    sn: 0,
    name: '(주)대우건설',
    openYn: true,
  },
  {
    sn: 0,
    name: '(주)신세계',
    openYn: true,
  },
  {
    sn: 0,
    name: '(주)카카오뱅크',
    openYn: true,
  },
  {
    sn: 0,
    name: '(주)한화',
    openYn: true,
  },
  {
    sn: 0,
    name: 'KB증권',
    openYn: true,
  },
  {
    sn: 0,
    name: 'KT',
    openYn: true,
  },
  {
    sn: 0,
    name: 'LG디스플레이',
    openYn: true,
  },
  {
    sn: 0,
    name: 'NH투자증권',
    openYn: true,
  },
  {
    sn: 0,
    name: 'S-OIL(주)',
    openYn: true,
  },
  {
    sn: 0,
    name: 'SK에너지',
    openYn: true,
  },
  {
    sn: 0,
    name: 'SK하이닉스',
    openYn: true,
  },
  {
    sn: 0,
    name: '국민건강보험공단',
    openYn: true,
  },
  {
    sn: 0,
    name: '기아(주)',
    openYn: true,
  },
];

const cx = classnames.bind(style);

interface InterviewPracticeQuestionSelectCompanyListProps {
  selectedCompanySn: number | null;
  setSelectedCompanySn: (sn: number) => void;
}

const InterviewPracticeQuestionSelectCompanyList = ({
  selectedCompanySn,
  setSelectedCompanySn,
}: InterviewPracticeQuestionSelectCompanyListProps) => {
  const { selectedQuestionList } = useInterview();
  const { data: userInfo } = useUserInfo();
  const { isAuthorityUser } = useCheckAuthority(userInfo);
  const { data: companyListData } = useCompanyList({ enabled: isAuthorityUser });
  const [searchText, setSearchText] = useState('');

  const handleCompanyClick = (sn: number) => setSelectedCompanySn(sn);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => setSearchText(event.currentTarget.value);

  const getFilteredCompanyList = () => {
    if (!isAuthorityUser) return MockData;
    if (searchText === '') return companyListData?.companyList;

    return companyListData?.companyList.filter((el) => el.name.includes(searchText));
  };

  const renderCompanyList = () => {
    const filteredCompanyList = getFilteredCompanyList();
    if (filteredCompanyList?.length === 0) {
      return (
        <div className={cx('companyListEmpty')}>
          <div className={cx('emptyTitle')}>찾는 기업이 없어요</div>
          <div className={cx('emptyDescription')}>다른 기업을 입력해 주세요</div>
        </div>
      );
    }

    return filteredCompanyList?.map((el) => {
      const selectedQuestionLen = selectedQuestionList?.filter(
        (v) => (v as CompanyQuestionVO)?.companySn === el.sn,
      ).length;

      return (
        <button
          key={el.sn}
          value={el.sn}
          className={cx(['companyListItem', { checked: selectedCompanySn === el.sn }, { disable: !el.openYn }])}
          onClick={(e) => {
            if (isAuthorityUser && el.openYn) handleCompanyClick(el.sn);
          }}
          disabled={!isAuthorityUser}
        >
          <span>{el.name}</span>
          {!!selectedQuestionLen && <div className={cx('selectedQuestionLen')}>{selectedQuestionLen}</div>}
        </button>
      );
    });
  };

  useEffect(() => {
    if (!selectedCompanySn && companyListData?.companyList) setSelectedCompanySn(companyListData?.companyList[0].sn);
  }, [companyListData?.companyList]);

  return (
    <div className={cx('companyListWrap')}>
      <div className={cx('searchInner')}>
        <div className={cx('companySearchWrap')}>
          <div className={cx('icon')}>
            <Icon name="searchLight" width={24} height={24} fill={Colors.C_COOL_GRAY_70} />
          </div>

          <input
            type={'text'}
            className={cx('searchInput')}
            placeholder={'기업을 검색해 주세요.'}
            value={searchText}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      <ul className={cx('companyList')}>{renderCompanyList()}</ul>
    </div>
  );
};

export default InterviewPracticeQuestionSelectCompanyList;
